console.log('[ProductCard] File loaded');
import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import Component from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

export class ProductCard extends Component {
  static imageVisibilityObserver = null;
  static blankPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  static getGlobalImageObserver() {
    if (ProductCard.imageVisibilityObserver) return ProductCard.imageVisibilityObserver;

    ProductCard.imageVisibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        if (card instanceof ProductCard) {
          if (entry.isIntersecting) {
            card.loadResources();
          } else {
            card.unloadResources();
          }
        }
      });
    }, {
      rootMargin: '600px 0px 600px 0px'
    });

    return ProductCard.imageVisibilityObserver;
  }

  constructor() {
    super();
    this.requiredRefs = ['productCardLink'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleQuickAdd();

    // FIX B4B: aplicar imagen correcta inmediatamente al conectar — evita el flash de color incorrecto
    if (this.closest('li[data-b4b-color]')) {
      this.querySelectorAll('img.b4b-image').forEach(img => {
        if (img.dataset.b4bSrc) {
          // B4B ya procesó este card — aplicar imagen correcta ahora mismo
          if (img.src !== img.dataset.b4bSrc) img.src = img.dataset.b4bSrc;
        } else {
          // B4B aún no procesó — ocultar para evitar mostrar el color incorrecto
          img.style.visibility = 'hidden';
        }
      });
    }

    ProductCard.getGlobalImageObserver().observe(this);

    this.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));
    this.addEventListener(ThemeEvents.variantSelected, this.handleVariantSelected.bind(this));
    this.addEventListener(SlideshowSelectEvent.eventName, this.handleSlideshowSelect.bind(this));
    mediaQueryLarge.addEventListener('change', this.handleQuickAdd.bind(this));
    this.addEventListener('click', this.navigateToProduct.bind(this));

    // FIX B4B (2/2)
    this._b4bObserver = new MutationObserver((mutations) => {
    mutations.forEach(m => {
      if (m.attributeName === 'data-b4b-src') {
        const img = m.target;
        if (img instanceof HTMLImageElement && img.dataset.b4bSrc && img.src !== img.dataset.b4bSrc) {
          img.src = img.dataset.b4bSrc;
          img.style.visibility = '';
        }
      }
    });
  });
  this._b4bObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['data-b4b-src'] });

    // Inicializar card con la variante color correcta al cargar
    requestYieldCallback(() => {
      this.initializeWithCheckedVariant();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ProductCard.getGlobalImageObserver().unobserve(this);
    if (this._b4bObserver) {
      this._b4bObserver.disconnect();
      this._b4bObserver = null;
    }
  }

  loadResources() {
  this.querySelectorAll('img').forEach(img => {
    const src = img.dataset.b4bSrc || img.dataset.offlineSrc;
    if (src) {
      img.src = src;
      img.style.visibility = '';
    } else if (!img.classList.contains('b4b-image')) {
      img.style.visibility = ''; // no-B4B: mostrar siempre
    }
    // B4B sin src aún: permanece oculta hasta que MutationObserver dispare
    if (img.dataset.offlineSrcset) img.srcset = img.dataset.offlineSrcset;
  });
  this.style.pointerEvents = '';
  const slideshow = this.querySelector('slideshow-component');
  if (slideshow && typeof slideshow.wakeUp === 'function') slideshow.wakeUp();
}

  unloadResources() {
  this.querySelectorAll('img').forEach(img => {
    if (img.src === ProductCard.blankPixel) return;
    // B4B con imagen correcta: solo ocultar, no blanquear el src
    if (img.classList.contains('b4b-image') && img.dataset.b4bSrc) {
      img.style.visibility = 'hidden';
      return;
    }
    // B4B sin imagen aún: blanquear sin guardar offlineSrc incorrecto
    if (img.classList.contains('b4b-image') && !img.dataset.b4bSrc) {
      img.src = ProductCard.blankPixel;
      img.removeAttribute('srcset');
      img.style.visibility = 'hidden';
      return;
    }
    // Resto de imágenes: comportamiento normal
    if (!img.dataset.offlineSrc && img.src) img.dataset.offlineSrc = img.src;
    if (!img.dataset.offlineSrcset && img.srcset) img.dataset.offlineSrcset = img.srcset;
    img.src = ProductCard.blankPixel;
    img.removeAttribute('srcset');
    img.style.visibility = 'hidden';
  });
  this.style.pointerEvents = 'none';
  const slideshow = this.querySelector('slideshow-component');
  if (slideshow && typeof slideshow.hibernate === 'function') slideshow.hibernate();
}

  preloadNextPreviewImage() {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;
    const currentSlide = slideshow.refs.slides?.[slideshow.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  handleQuickAdd() {
    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.fetchProductPageHandler);
      this.addEventListener('focusin', this.fetchProductPageHandler);
    }
  }

  fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.refs.productCardLink.href);
  };

  handleVariantSelected(event) {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  }

  handleVariantUpdate(event) {
    event.stopPropagation();
    this.updatePrice(event);
    this.updateProductUrl(event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.updateVariantImages();
    this.updateOverflowList();
  }

  updateOverflowList() {
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList) return;
    requestAnimationFrame(() => {
      overflowList.dispatchEvent(new CustomEvent('reflow', { bubbles: true }));
    });
  }

  updatePrice(event) {
    const prices = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = prices[1] || prices[0];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);
    if (newPriceElement && priceContainer) morph(priceContainer, newPriceElement);
  }

  updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') return;
      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption || !slideshow) return;
    const selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (selectedImageId) {
      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  initializeWithCheckedVariant() {
  const picker = this.querySelector('swatches-variant-picker-component');
  if (!picker) return;

  const checkedSwatch = picker.querySelector('input[type="radio"]:checked');
  if (!checkedSwatch) return;

  const productUrl = picker.dataset.productUrl?.split('?')[0];
  if (!productUrl) return;

  // Actualizar slideshow inmediatamente con el mediaId del swatch marcado
  const mediaId = checkedSwatch.dataset.optionMediaId;
  if (mediaId) {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select({ id: mediaId }, undefined, { animate: false });
    }
  }

  const promoCollections = ['/collections/special-prices-woman', '/collections/special-prices-man'];
  const isPromoCollection = promoCollections.some(path => window.location.pathname.includes(path));

  const allSwatches = [...picker.querySelectorAll('input[type="radio"]')];

  const fetchSectionPrice = (variantId) => {
  const url = new URL(productUrl, window.location.origin);

  url.searchParams.set('variant', variantId);
  url.searchParams.set('section_id', 'section-rendering-product-card');
  return fetch(url.href)
    .then(r => r.text())
    .then(html => new DOMParser().parseFromString(html, 'text/html'));
  };


  const trySwatchesInOrder = async (swatches) => {
    for (const swatch of swatches) {
      const variantId = swatch.dataset.firstAvailableOrFirstVariantId;
      if (!variantId) continue;

      const doc = await fetchSectionPrice(variantId);
      const hasDiscount = !!doc.querySelector('product-price .compare-at-price');

      if (!isPromoCollection || hasDiscount) {
        const prices = this.querySelectorAll(`product-price [ref='priceContainer']`);
        const priceContainer = prices[1] || prices[0];
        const newPrice = doc.querySelector(`product-price [ref='priceContainer']`);
        if (newPrice && priceContainer) morph(priceContainer, newPrice);

        if (swatch !== checkedSwatch) {
          const newMediaId = swatch.dataset.optionMediaId;
          if (newMediaId) {
            const slideshow = this.querySelector('slideshow-component');
            if (slideshow && typeof slideshow.select === 'function') {
              slideshow.select({ id: newMediaId }, undefined, { animate: false });
            }
          }
        }
        return;
      }
    }

    // Ningún color tiene descuento → ocultar card
    if (isPromoCollection) {
      this.style.setProperty('display', 'none', 'important');
      this.closest('li.product-grid__item')?.style.setProperty('display', 'none', 'important');
    }
  };

  const orderedSwatches = [
    checkedSwatch,
    ...allSwatches.filter(s => s !== checkedSwatch)
  ];

    requestYieldCallback(() => trySwatchesInOrder(orderedSwatches));
  }



  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  handleSlideshowSelect() {}

  navigateToProduct(event) {
    if (!(event.target instanceof Element) || this.hasAttribute('data-no-navigation')) return;
    if (event.defaultPrevented) return;
    if (this.#eventTargetsInteractiveElement(event)) return;
    if (this.#eventTargetsSlideshowControl(event)) return;

    const link = this.refs.productCardLink;
    if (!link || !link.href) return;

    if (!event.target.closest('a')) {
      window.location.href = link.href;
    }
  }

  #eventTargetsInteractiveElement(event) {
    const selectors = 'button, input, label, select, textarea, summary, [role="button"], [tabindex]';
    return this.#eventPathContains(event, selectors);
  }

  #eventTargetsSlideshowControl(event) {
    const selectors =
      'slideshow-arrows, slideshow-controls, [ref="previous"], [ref="next"], [ref="dots[]"], [ref="thumbnails[]"]';
    return this.#eventPathContains(event, selectors);
  }

  #eventPathContains(event, selectors) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.some((node) => node instanceof Element && node.closest(selectors));
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantUpdate, this.handleCardVariantUrlUpdate.bind(this));
  }

  handleCardVariantUrlUpdate() {
    const card = this.closest('product-card');
    if (this.pendingVariantId && card) {
      const currentUrl = new URL(card.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      card.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.name?.includes('-swatch')) {
      super.variantChanged(event);
      return;
    }
    const swatch = event.target;
    const firstAvailableId = swatch.dataset.firstAvailableOrFirstVariantId;
    if (firstAvailableId) {
      event.stopPropagation();
      const productUrl = this.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableId);
      window.location.href = url.toString();
    } else {
      super.variantChanged(event);
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
